import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/lib/router";
import { X, ArrowUp, Sparkle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { CONCIERGE } from "@/i18n/concierge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "vornx-concierge-sid";
const MSG_KEY = "vornx-concierge-msgs";

// Minimal markdown: **bold** and [label](/internal-path).
const Rich = ({ text, onNavigate }) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((p, i) => {
    const b = p.match(/^\*\*(.+)\*\*$/);
    if (b) return <strong key={i} className="font-medium text-gold-soft">{b[1]}</strong>;
    const l = p.match(/^\[(.+)\]\((.+)\)$/);
    if (l) {
      return l[2].startsWith("/")
        ? <Link key={i} to={l[2]} onClick={onNavigate} className="text-gold underline underline-offset-4 hover:text-white">{l[1]}</Link>
        : <a key={i} href={l[2]} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-4">{l[1]}</a>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

const Bubble = ({ m, onNavigate }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] whitespace-pre-line px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-gold/15 text-white" : "border border-gold/15 bg-white/[0.03] text-white/80"}`}>
      {m.text ? <Rich text={m.text} onNavigate={onNavigate} /> : <span className="concierge-dots" aria-label="..."><i /><i /><i /></span>}
    </div>
  </div>
);

export const ConciergeChat = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(() => { try { return JSON.parse(sessionStorage.getItem(MSG_KEY)) || []; } catch { return []; } });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    const h = (e) => setBanner(!!e.detail?.visible);
    window.addEventListener("vornx:cookie", h);
    return () => window.removeEventListener("vornx:cookie", h);
  }, []);
  useEffect(() => { sessionStorage.setItem(MSG_KEY, JSON.stringify(msgs)); endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const reset = () => { setMsgs([]); sessionStorage.removeItem(SID_KEY); };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    const append = (s) => setMsgs((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + s }; return c; });
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, language: lang, session_id: sessionStorage.getItem(SID_KEY) || undefined }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const d = JSON.parse(line.slice(6));
          if (d.session_id) sessionStorage.setItem(SID_KEY, d.session_id);
          if (d.delta) append(d.delta);
          if (d.error) append(t(CONCIERGE.error));
        }
      }
    } catch {
      append(t(CONCIERGE.error));
    } finally {
      setBusy(false);
    }
  };

  const bottom = banner ? "bottom-[15rem] sm:bottom-[12.5rem]" : "bottom-24";

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="concierge-open-btn" aria-label={t(CONCIERGE.title)}
          className={`group fixed right-6 z-50 flex h-14 items-center gap-2 rounded-full border border-gold/50 bg-ink/90 pl-4 pr-5 text-gold shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md transition-[transform,bottom,background-color] duration-300 hover:scale-105 hover:bg-ink ${bottom}`}>
          <Sparkle size={20} weight="light" className="transition-transform duration-500 group-hover:rotate-90" />
          <span className="hidden text-[11px] uppercase tracking-widest sm:inline">{t(CONCIERGE.launcher)}</span>
        </button>
      )}
      {open && (
        <div data-testid="concierge-panel" role="dialog" aria-label={t(CONCIERGE.title)}
          className="concierge-in fixed inset-x-3 bottom-3 top-20 z-[70] flex flex-col border border-gold/30 bg-[#0b0b0b]/95 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.9)] backdrop-blur-xl sm:inset-x-auto sm:right-6 sm:top-auto sm:h-[600px] sm:max-h-[calc(100vh-7rem)] sm:w-[400px]">
          <div className="flex items-center justify-between border-b border-gold/15 px-5 py-4">
            <div>
              <p className="font-display text-lg text-gold">{t(CONCIERGE.title)}</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40">{t(CONCIERGE.subtitle)}</p>
            </div>
            <div className="flex items-center gap-1">
              {msgs.length > 0 && !busy && <button onClick={reset} data-testid="concierge-reset-btn" aria-label={t(CONCIERGE.reset)} title={t(CONCIERGE.reset)} className="p-2.5 text-white/40 transition-colors hover:text-gold"><ArrowCounterClockwise size={18} /></button>}
              <button onClick={() => setOpen(false)} data-testid="concierge-close-btn" aria-label="Chiudi" className="p-2.5 text-white/60 transition-colors hover:text-gold"><X size={20} /></button>
            </div>
          </div>
          <div data-testid="concierge-messages" className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
            <Bubble m={{ role: "assistant", text: t(CONCIERGE.greeting) }} />
            {msgs.length === 0 && (
              <div className="flex flex-col items-start gap-2 pt-1">
                {CONCIERGE.suggestions.map((s, i) => (
                  <button key={i} onClick={() => send(t(s))} data-testid={`concierge-suggestion-${i}`} className="border border-gold/30 px-3 py-2 text-left text-xs text-gold/90 transition-colors hover:bg-gold hover:text-ink">{t(s)}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} onNavigate={() => setOpen(false)} />)}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 border-t border-gold/15 p-3">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} maxLength={800} rows={1}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t(CONCIERGE.placeholder)} data-testid="concierge-input"
              className="max-h-28 flex-1 resize-none bg-transparent px-2 py-2.5 text-base text-white placeholder:text-white/30 focus:outline-none sm:text-sm" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="concierge-send-btn" aria-label="Invia"
              className="flex h-10 w-10 shrink-0 items-center justify-center bg-gold text-ink transition-opacity disabled:opacity-30"><ArrowUp size={18} weight="bold" /></button>
          </form>
          <p className="px-5 pb-3 text-[10px] text-white/30">{t(CONCIERGE.disclaimer)}</p>
        </div>
      )}
    </>
  );
};
